// src/pages/ClientProfile.js
import React, { useState, useEffect } from 'react';
import {
    Container,
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Alert,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress
} from '@mui/material';
import {
    Person as PersonIcon,
    Save as SaveIcon,
    VpnKey as VpnKeyIcon,
    ExitToApp as LogoutIcon,
    Description as DescriptionIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useClientAuth } from '../contexts/ClientAuthContext';
import FiberLoader from '../components/common/FiberLoader';

const ChangePasswordDialog = ({ open, onClose, onSubmit }) => {
    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setPasswords({
            ...passwords,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (passwords.newPassword !== passwords.confirmPassword) {
            setError('Las contraseñas nuevas no coinciden');
            return;
        }

        if (passwords.newPassword.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        setLoading(true);
        try {
            await onSubmit(passwords);
            onClose();
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Cambiar Contraseña</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Contraseña Actual"
                        type="password"
                        name="currentPassword"
                        value={passwords.currentPassword}
                        onChange={handleChange}
                        required
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Nueva Contraseña"
                        type="password"
                        name="newPassword"
                        value={passwords.newPassword}
                        onChange={handleChange}
                        required
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Confirmar Contraseña"
                        type="password"
                        name="confirmPassword"
                        value={passwords.confirmPassword}
                        onChange={handleChange}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <VpnKeyIcon />}
                    >
                        Cambiar Contraseña
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

const ClientProfile = () => {
    const { client, logoutClient } = useClientAuth();
    const [profileData, setProfileData] = useState({
        email: '',
        phone: '',
        address: '',
        department: '',
        province: '',
        district: ''
    });
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [contractStatus, setContractStatus] = useState(null);
    const [acceptingContract, setAcceptingContract] = useState(false);

    useEffect(() => {
        fetchProfileData();
        fetchNotifications();
        fetchContractStatus();
    }, []);

    const fetchContractStatus = async () => {
        try {
            const token = localStorage.getItem('clientToken');
            const res = await fetch('/api/client/profile/contract-status', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setContractStatus(data);
            }
        } catch (err) {
            console.error('Error fetching contract status:', err);
        }
    };

    const handleAcceptContract = async () => {
        setAcceptingContract(true);
        setError('');
        setSuccess('');
        try {
            const token = localStorage.getItem('clientToken');
            const res = await fetch('/api/client/profile/accept-contract', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Error al aceptar el contrato');
            setSuccess('✅ ¡Has aceptado exitosamente tu contrato de servicio OSIPTEL!');
            fetchContractStatus();
        } catch (err) {
            setError(err.message);
        } finally {
            setAcceptingContract(false);
        }
    };

    const fetchProfileData = async () => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/profile', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener datos del perfil');
            }

            const data = await response.json();
            setProfileData({
                email: data.email || '',
                phone: data.phone || '',
                address: data.address || '',
                department: data.department || '',
                province: data.province || '',
                district: data.district || ''
            });
        } catch (error) {
            console.error('Error fetching profile:', error);
            setError('Error al cargar datos del perfil');
        } finally {
            setLoading(false);
        }
    };

    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/profile/notifications', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setNotifications(data);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const handleProfileChange = (e) => {
        setProfileData({
            ...profileData,
            [e.target.name]: e.target.value
        });
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setSaveLoading(true);
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/profile', {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(profileData)
            });

            if (!response.ok) {
                throw new Error('Error al actualizar perfil');
            }

            setSuccess('Perfil actualizado exitosamente');
        } catch (error) {
            console.error('Error updating profile:', error);
            setError(error.message || 'Error al actualizar perfil');
        } finally {
            setSaveLoading(false);
        }
    };

    const handlePasswordChange = async (passwordData) => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/profile/change-password', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    currentPassword: passwordData.currentPassword,
                    newPassword: passwordData.newPassword
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Error al cambiar contraseña');
            }

            setSuccess('Contraseña actualizada exitosamente');
        } catch (error) {
            console.error('Error changing password:', error);
            throw error;
        }
    };

    const markNotificationAsRead = async (notificationId) => {
        try {
            const token = localStorage.getItem('clientToken');
            await fetch(`/api/client/profile/notifications/${notificationId}/read`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // Actualizar notificaciones localmente
            setNotifications(notifications.map(n =>
                n.id === notificationId ? { ...n, read: true } : n
            ));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    if (loading) {
        return (
            <FiberLoader text="Obteniendo información de tu perfil..." subtext="Cargando datos del abonado de fibra óptica..." fullPage />
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Mi Perfil
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}

            <Grid container spacing={4}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3 }} elevation={2}>
                        <Typography variant="h6" gutterBottom>Información Personal</Typography>
                        <form onSubmit={handleProfileSubmit}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Nombre Completo"
                                        value={client?.fullName || ''}
                                        disabled
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Correo Electrónico"
                                        name="email"
                                        value={profileData.email}
                                        onChange={handleProfileChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Teléfono"
                                        name="phone"
                                        value={profileData.phone}
                                        onChange={handleProfileChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Dirección"
                                        name="address"
                                        value={profileData.address}
                                        onChange={handleProfileChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Departamento"
                                        name="department"
                                        value={profileData.department}
                                        onChange={handleProfileChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Provincia"
                                        name="province"
                                        value={profileData.province}
                                        onChange={handleProfileChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Distrito"
                                        name="district"
                                        value={profileData.district}
                                        onChange={handleProfileChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => setPasswordDialogOpen(true)}
                                            startIcon={<VpnKeyIcon />}
                                        >
                                            Cambiar Contraseña
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={saveLoading}
                                            startIcon={saveLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                                        >
                                            Guardar Cambios
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    </Paper>

                    <Paper sx={{ p: 3, mt: 3, border: '1px solid rgba(239, 68, 68, 0.2)', bgcolor: 'rgba(239, 68, 68, 0.02)', borderRadius: 2 }} elevation={2}>
                        <Typography variant="h6" gutterBottom sx={{ color: 'error.main', fontWeight: 'bold' }}>
                            Sesión Activa
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Si deseas salir de tu cuenta en este dispositivo, puedes cerrar sesión de forma segura.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={logoutClient}
                            startIcon={<LogoutIcon />}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                        >
                            Cerrar Sesión
                        </Button>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon color="primary" /> Contrato de Servicio OSIPTEL
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Consulta y acepta tu contrato de servicio de acceso a internet.
                        </Typography>

                        {contractStatus?.status === 'accepted' ? (
                            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                                <strong>Contrato Aceptado y Vigente ✔</strong>
                                <br />
                                <Typography variant="caption" color="textSecondary">
                                    Conformidad registrada el {new Date(contractStatus.generatedAt || Date.now()).toLocaleDateString('es-PE')}
                                </Typography>
                            </Alert>
                        ) : (
                            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                                Tienes tu contrato pendiente de conformidad.
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {(contractStatus?.contractDocumentId || contractStatus?.documentId) && (
                                <>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<DescriptionIcon />}
                                        onClick={() => {
                                            const token = localStorage.getItem('clientToken');
                                            const docId = contractStatus.contractDocumentId || contractStatus.documentId;
                                            window.open(`/api/documents/${docId}/pdf?format=A4&token=${token}`, '_blank');
                                        }}
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                                    >
                                        📜 Descargar Resumen Contrato OSIPTEL (PDF)
                                    </Button>

                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={<DescriptionIcon />}
                                        onClick={() => {
                                            const token = localStorage.getItem('clientToken') || localStorage.getItem('token');
                                            const docId = contractStatus.contractDocumentId || contractStatus.documentId;
                                            window.open(`/api/documents/${docId}/full-pdf?token=${token}`, '_blank');
                                        }}
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                                    >
                                        📚 Descargar Contrato Completo (34 Cláusulas PDF)
                                    </Button>
                                </>
                            )}

                            {contractStatus?.anexo1DocumentId && contractStatus.anexo1DocumentId !== contractStatus.contractDocumentId && (
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<DescriptionIcon />}
                                    onClick={() => {
                                        const token = localStorage.getItem('clientToken');
                                        window.open(`/api/documents/${contractStatus.anexo1DocumentId}/pdf?format=A4&token=${token}`, '_blank');
                                    }}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                                >
                                    📋 Descargar Ficha de Instalación - Anexo 1 (PDF)
                                </Button>
                            )}

                            {contractStatus?.status !== 'accepted' && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={acceptingContract ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                                    onClick={handleAcceptContract}
                                    disabled={acceptingContract}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                                >
                                    {acceptingContract ? 'Registrando...' : '✅ Aceptar Contrato y Términos'}
                                </Button>
                            )}
                        </Box>
                    </Paper>

                    <Paper sx={{ p: 3 }} elevation={2}>
                        <Typography variant="h6" gutterBottom>Mi Plan</Typography>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="textSecondary">Plan Actual</Typography>
                            <Typography variant="body1">{client?.plan || 'No disponible'}</Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="textSecondary">Velocidad</Typography>
                            <Typography variant="body1">
                                {client?.speeds ? `${client.speeds.download}/${client.speeds.upload} Mbps` : 'No disponible'}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom>Últimas Notificaciones</Typography>
                        {notifications.length > 0 ? (
                            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                                {notifications.slice(0, 5).map((notification) => (
                                    <Paper
                                        key={notification.id}
                                        sx={{
                                            p: 2,
                                            mb: 1,
                                            backgroundColor: notification.read ? 'background.default' : 'background.paper',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => markNotificationAsRead(notification.id)}
                                        elevation={notification.read ? 0 : 1}
                                    >
                                        <Typography variant="subtitle2">{notification.title}</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {notification.message}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                                            {new Date(notification.created_at).toLocaleString()}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                No hay notificaciones
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <ChangePasswordDialog
                open={passwordDialogOpen}
                onClose={() => setPasswordDialogOpen(false)}
                onSubmit={handlePasswordChange}
            />
        </Container>
    );
};

export default ClientProfile;